import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getIsWeatherOverrideActive,
  getWeatherOverrideRemainingTime,
} from "../../sites_configuration/safeOverride/SafeOverrideApi";
import { resetSiteSpecificData } from "./GlobalActions";
import { beginRequest, completeRequest, rejectRequest, RequestState } from "./RequestState";
import { LoadingState } from "../LoadingState";

interface ISafeOverrideState extends RequestState {
  inOverrideSafeMode: boolean;
  expiredAt: number;
  siteId: string;
}

const initialState: ISafeOverrideState = {
  loadingState: LoadingState.None,
  inOverrideSafeMode: false,
  expiredAt: 0,
  siteId: "",
};

const safeOverrideSlice = createSlice({
  name: "safeOverride",
  initialState,
  reducers: {
    updateSafeOverrideState: (state, action: PayloadAction<ISafeOverrideState>) => {
      state.inOverrideSafeMode = action.payload.inOverrideSafeMode;
      state.expiredAt = action.payload.expiredAt;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchSafeOverride.pending, (state, action) => {
        const nextState =
          state.siteId === action.meta.arg ? state : { ...initialState, siteId: action.meta.arg };
        beginRequest(nextState, action.meta.requestId);
        return nextState;
      })
      .addCase(fetchSafeOverride.fulfilled, (state, action) => {
        if (state.siteId !== action.meta.arg || !completeRequest(state, action.meta.requestId)) {
          return;
        }
        state.inOverrideSafeMode = action.payload.inOverrideSafeMode;
        if (action.payload.remainingTime != null) {
          state.expiredAt = Date.now() + action.payload.remainingTime;
        }
      })
      .addCase(fetchSafeOverride.rejected, (state, action) => {
        rejectRequest(state, action);
      })
      .addCase(resetSiteSpecificData, () => {
        return initialState;
      });
  },
});

export const fetchSafeOverride = createAsyncThunk(
  "safeOverride/fetch",
  async (siteId: string, { signal }) => {
    const isOverrideActive = await getIsWeatherOverrideActive(siteId, signal);
    return {
      inOverrideSafeMode: isOverrideActive,
      ...(isOverrideActive && {
        remainingTime: await getWeatherOverrideRemainingTime(siteId, signal),
      }),
    };
  }
);
export const { updateSafeOverrideState } = safeOverrideSlice.actions;
export const safeOverrideReducer = safeOverrideSlice.reducer;
